/**
 * The privacy notice, as data. OPERATOR-AUTHORED — byte-frozen for the implementer.
 *
 * WHY THIS FILE EXISTS SEPARATELY. `PrivacyScreen.tsx` renders the notice; this file IS the
 * notice. Splitting them makes the authorship boundary a whole file rather than a region, so an
 * implementer cannot reword a legally binding statement while "tidying" a component.
 *
 * EVERY FACTUAL CLAIM BELOW IS SOURCED. None of it is generated plausible-sounding text:
 *  - Controller identity: the Companies House record for company 12379620, read 2026-09-04 —
 *    THE FAIRY TAILS DC LTD, active, private limited company, registered office as written below.
 *  - Phone and email: interpolated from `../model/contact`, never retyped. That is not only the
 *    project's single-source rule — `contact.test.tsx` scans every file under `packages/web/src`
 *    except `contact.ts` and fails on any phone-shaped string outside Ofcom's reserved ranges, so
 *    a hardcoded number here would (correctly) break the build.
 *  - Categories of data: the record schema in `@ft/shared` types and the JotForm field mapping.
 *  - Processors: what the code actually calls — JotForm's European service
 *    (`JOTFORM_API_BASE` = https://eu-api.jotform.com), Resend for outbound email, and the
 *    Hostinger virtual server this portal is deployed to.
 *  - Retention: SIX YEARS after the last visit — owner decision, 2026-09-04, tied to the
 *    limitation period for contract and negligence claims in England and Wales.
 *
 * THE ICO'S TELEPHONE NUMBER IS DELIBERATELY OMITTED. It is phone-shaped, so including it would
 * trip the same guard, and weakening that guard to admit one number would leave it unable to
 * catch a real client's. The ICO's complaint URL is a complete contact route on its own.
 *
 * CORRECTED 2026-09-04, before implementation, by the pre-flight critique. The first draft of
 * the emergency-contact sentence said those details are "shown only to staff who need them" and
 * named only a name and a telephone number. BOTH were wrong, and wrong in a legally binding
 * document: the reveal route sits behind a CUSTOMER session, so the household can reveal its own
 * emergency contact, and the payload carries name, address, postcode, phone AND email. This is
 * exactly the "confidently wrong" failure the original PrivacyScreen header warned about. The
 * sentence now states who can actually see it and what is actually held.
 *
 * FOUR CLAIMS ARE ABOUT THE BUSINESS RATHER THAN THE CODE, and were decided rather than invented:
 *  1. The licensing sentence is worded to be true whatever the exact licence class — it says
 *     records are kept for licensing and animal welfare purposes, not that a named licence is
 *     held under a named statute.
 *  2. "We do not sell your information" is asserted. If any marketing system anywhere in the
 *     business ever does, this sentence must change first.
 *  3. NO Data Protection Officer is claimed, because there is no evidence one is appointed. A
 *     business of this size is unlikely to need one. A contact route is given instead.
 *  4. Deletion after six years is written as a commitment. It has to be keepable in JotForm,
 *     which is where the records actually live — not only in this portal's database. There is no
 *     erasure job today; `retired_at` is a soft delete.
 *  5. The controller identity — company number 12379620 and the registered office — was read from
 *     the Companies House record, but nobody inside this repository can verify it. It is the most
 *     load-bearing line in the document, so Kam signs it too.
 *  6. The international-transfer sentence says our suppliers are outside the UK, the services are
 *     hosted in Europe, and our contracts with them provide the protection. Kam should confirm the
 *     supplier agreements (JotForm's and Resend's data processing terms) actually say so.
 *
 * CORRECTED AGAIN 2026-09-05, by the blind review, before merge:
 *  - "every single reveal is recorded" became "we record every reveal". The first is a guarantee
 *    the code does not make: `audit()` is best-effort, so a failed insert still returns the
 *    contact. Same class of error as the "shown only to staff" claim.
 *  - the "what we hold" section gained the PARTNER's name, mobile and email (a second identifiable
 *    person, exactly the shape of the emergency-contact omission) and the sign-in/IP records the
 *    audit log actually keeps. Both were held and neither was disclosed.
 *  - an Article 13(1)(f) sentence was added: the suppliers are US companies.
 */

import { PHONE_DISPLAY, SUPPORT_EMAIL } from '../model/contact'

export interface PrivacySection {
  readonly id: string
  readonly heading: string
  readonly paragraphs: readonly string[]
}

/** How to reach us, written once and reused, so the notice can never drift from the app. */
const CONTACT_SENTENCE = `call us on ${PHONE_DISPLAY} or email ${SUPPORT_EMAIL}`

/**
 * The eight Article 13 sections, in the order a person reads them rather than the order the
 * legislation lists them. The ids and headings match what the screen has always carried, so the
 * existing headings test keeps passing untouched.
 */
export const PRIVACY_SECTIONS: readonly PrivacySection[] = [
  {
    id: 'who',
    heading: 'Who we are',
    paragraphs: [
      'The Fairy Tails is a trading name of THE FAIRY TAILS DC LTD, a company registered in England and Wales with company number 12379620. Our registered office is The Barn, Barley Lane, Fairlight, Hastings, TN35 5DT.',
      `We decide how the information described here is used, which makes us responsible for it in law. If you have any question about this notice, or about what we hold on you and your dog, ${CONTACT_SENTENCE}.`,
    ],
  },
  {
    id: 'what',
    heading: 'What we hold about you and your dog',
    paragraphs: [
      'About you: your name, your home address, your telephone numbers and your email address. If you gave us a second contact for your household — a partner or another member of the family — we hold their name, mobile number and email address too.',
      'About your use of this portal: a record of when you signed in and of certain actions you took, including the internet address (IP address) you used at the time. We keep that so we can see who looked at what, which is part of how we protect your information.',
      'About your dog: their name, breed, age, sex, colour and description, their microchip number, and any photographs you have given us.',
      'About your dog’s health and care: vaccination and treatment dates, worming and flea treatments, medication and dosage, allergies, medical and dietary notes, whether they are neutered, your veterinary practice and your insurance details.',
      'About how your dog is looked after: feeding requirements and portion sizes, which meals they have, behaviour notes, and anything else you have told us they need.',
      'We also hold the details of the emergency contact you gave us: their name, address, postcode, telephone number and email address. In this portal those details are hidden by default. They are revealed only when someone asks to see them — you, on your own record, or one of our administrators — and we record every reveal. Our ordinary staff console does not show them at all.',
    ],
  },
  {
    id: 'why',
    heading: 'Why we hold it',
    paragraphs: [
      'We hold this information so that we can arrange and provide the services you have asked for — daycare, boarding, training, grooming and walking.',
      'We use it to look after your dog safely: to feed them correctly, to give any medication exactly as you have instructed, and to know what they can and cannot have.',
      'We use it to reach you, or your emergency contact, if something happens while your dog is with us, and to get veterinary help quickly and give a vet what they need to know.',
      'We also use it to keep the records we are required to keep for licensing and animal welfare purposes, and to deal with any complaint, claim or insurance matter.',
    ],
  },
  {
    id: 'basis',
    heading: 'Our lawful basis',
    paragraphs: [
      'We rely on three of the lawful bases in Article 6 of the UK GDPR.',
      'Performance of a contract, for everything involved in providing the service you have booked: your contact details, your dog’s care requirements, and their feeding and medication instructions.',
      'Legal obligation, for the records we are required to keep for licensing and animal welfare purposes.',
      'Legitimate interests, for holding an emergency contact so that someone can be reached if you cannot be, and for keeping records long enough to deal with a claim or a complaint. We have weighed those interests against your own and hold only what is needed.',
    ],
  },
  {
    id: 'sharing',
    heading: 'Who else sees it',
    paragraphs: [
      'A vet, if your dog needs treatment while in our care.',
      'Your emergency contact, if we cannot reach you.',
      'Our insurer, if there is a claim, and our local authority licensing officer on inspection, where they are entitled to see our records.',
      'The suppliers who run our systems for us. Our forms and records are held on JotForm, on their European service; the emails this portal sends go through Resend; and the portal itself runs on a server we rent from Hostinger. They act on our instructions and may not use your information for their own purposes.',
      'Those suppliers are companies based outside the United Kingdom, although the services we use are hosted in Europe. Where your information is handled outside the UK, it is protected by the terms of our contracts with them, which require them to keep it secure and to use it only as we instruct.',
      'We do not sell your information, and we do not use it for advertising.',
    ],
  },
  {
    id: 'retention',
    heading: 'How long we keep it',
    paragraphs: [
      'We keep your dog’s record for six years after the last time they came to us, and then we delete it.',
      'Six years is the period in which a claim about a contract, or about negligence, can normally be brought in England and Wales. Keeping records for that long means we can answer one properly. Where the law requires us to keep something for longer, we keep that item for as long as the law says and no longer.',
    ],
  },
  {
    id: 'rights',
    heading: 'Your rights',
    paragraphs: [
      'You have the right to see a copy of the information we hold about you, and to have anything wrong put right. You can change your dog’s feeding details yourself in this portal, and you can ask us to correct anything else.',
      'You can ask us to delete your information where we do not have to keep it, and you can ask us to restrict how we use it or object to a particular use — including anything we do on the basis of legitimate interests.',
      'You can also ask for a portable copy of the information you gave us, in a common format.',
      `To use any of these rights, ${CONTACT_SENTENCE}. We will respond within one month, and there is no charge.`,
    ],
  },
  {
    id: 'complain',
    heading: 'How to complain',
    paragraphs: [
      'If you are unhappy with how we have handled your information, please tell us first — we would much rather put it right.',
      'You can also complain to the Information Commissioner’s Office, the UK’s data protection regulator, at ico.org.uk/make-a-complaint. Complaining to us first does not affect your right to go to them.',
    ],
  },
]
